import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { getFixtures } from "../../../../lib/api";
import { handleDateFormat } from "../../../../lib";
import FilterFixturesLoadingSkeleton from "../FilterFixturesLoadingSkeleton";
import FilterSelection from "../../../../experiments/BubbleJourney/components/FilterSelection";
import FilterCategory from "../FilterCategory";
import styles from "./FilterFixtures.module.scss";
import {
  AnalyticsTypeMap,
  DateTextMap,
  FIXTURES_VIEW,
  LOCATION_MAP,
} from "../../../../constants";
import { sendAnalyticsEvent } from "../../../../lib/postMessage";
import { DESELECT, FILTERS, SELECT } from "../../../../constants/analytics";

const FilterFixtures = () => {
  const dispatch = useDispatch();
  const { prefetchedFilters } = useSelector((state) => state.filter);
  const { activeView } = useSelector((state) => state.view);
  const [isLoading, setIsLoading] = useState(true);
  const [competitions, setCompetitions] = useState([]);
  const todaysDate = handleDateFormat();

  const getFilterFixtures = async () => {
    const res = await getFixtures();

    if (res.code === 0) {
      setCompetitions(res.competitions);
      // Store number of fixtures available that comes back from the api in localStorage
      // rather than store in context so we don't trigger a re-render.
      // We use this value in SelectionsContainer to check if the number of filter fixtures
      // matches it, if so we don't send a fixtures param for the getSelections api call to
      // get search results.
      localStorage.setItem(
        "numberOfAvailableFixtures",
        res.competitions.reduce(
          (sum, competition) => sum + competition.fixtures.length,
          0
        )
      );
      // Add delay to setIsLoading being set to false when data is available so we see the loading animation
      setTimeout(() => {
        setIsLoading(false);
      }, 1200);
    } else {
      // Make sure we set setIsLoading back to false if the API call fails so we don't stay in a loading loop
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFilterFixtures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If “Today” has been selected on the Date filter we need to filter the response from the API, to only show competitions kicking off today in the list
  // If “Tomorrow” has been selected on the Date filter we need to filter the response from the API, to only show competitions kicking off tomorrow in the list
  const filteredCompetitions = () => {
    if (prefetchedFilters.date.length === 1) {
      if (prefetchedFilters.date[0] === "today") {
        return competitions.filter((competition) =>
          competition.fixtures.some(
            (fixture) => handleDateFormat(fixture.date) === handleDateFormat()
          )
        );
      }

      if (prefetchedFilters.date[0] === "tomorrow") {
        return competitions.filter((competition) =>
          competition.fixtures.some(
            (fixture) => handleDateFormat(fixture.date) !== handleDateFormat()
          )
        );
      }
    }

    return competitions;
  };

  const filterCompetitions = filteredCompetitions();

  // If “Today” has been selected on the Date filter we need to filter the response from the API, to only show matches kicking off today in the list
  // If “Tomorrow” has been selected on the Date filter we need to filter the response from the API, to only show matches kicking off tomorrow in the list
  const filteredFixtures = (fixtures) => {
    if (prefetchedFilters.date.length === 1) {
      if (prefetchedFilters.date[0] === "today") {
        return fixtures.filter(
          (competition) =>
            handleDateFormat(competition.date) === handleDateFormat()
        );
      }

      if (prefetchedFilters.date[0] === "tomorrow") {
        return fixtures.filter(
          (competition) =>
            handleDateFormat(competition.date) !== handleDateFormat()
        );
      }
    }

    return fixtures;
  };

  // Handle Fixture Selection differently
  const handleFixtureFilterSelection = (fixture) => {
    const analyticsEvent = {
      event: FILTERS,
      type: AnalyticsTypeMap.get(FIXTURES_VIEW) ?? FIXTURES_VIEW,
      value: `${fixture.homeTeamName} v ${fixture.awayTeamName}`,
      location: LOCATION_MAP.get(activeView) || "",
    };
    const fixtureDate = handleDateFormat(fixture.date);
    const date = fixtureDate === todaysDate ? "today" : "tomorrow";
    // Split adding to the fixtures object into two manageable arrays today and tomorrow
    // To find the active selection, we combine both arrays together and look up the id of the fixture
    // We can remove any fixtures in today or tomorrow array if the date filter chosen is opposite by
    // removing that array from the object.

    // if an already active selection we should remove from the relevant fixture array here
    if (prefetchedFilters.fixtures[date].includes(fixture.id)) {
      dispatch({
        type: "REMOVE_FROM_FIXTURES_FILTER_SELECTIONS",
        payload: {
          filter: FIXTURES_VIEW,
          date: date,
          fixture: fixture.id,
        },
      });
      sendAnalyticsEvent({ ...analyticsEvent, action: DESELECT });
    } else {
      dispatch({
        type: "ADD_TO_FIXTURES_FILTER_SELECTIONS",
        payload: {
          filter: FIXTURES_VIEW,
          date: date,
          fixture: fixture.id,
        },
      });
      sendAnalyticsEvent({ ...analyticsEvent, action: SELECT });
    }
  };

  const isFixtureSelected = (fixture) => {
    const fixtureDate = handleDateFormat(fixture.date);
    const date = fixtureDate === todaysDate ? "today" : "tomorrow";
    return prefetchedFilters.fixtures[date].includes(fixture.id);
  };

  if (isLoading) return <FilterFixturesLoadingSkeleton />;

  if (!filterCompetitions.length) return null;

  return (
    <>
      <div className={styles.fixturesSelections}>
        {filterCompetitions.map((competition) => {
          const selections = filteredFixtures(competition.fixtures);
          return (
            <FilterCategory
              key={competition.id}
              categoryName={competition.name}
              selections={selections}
              activeFilterView={FIXTURES_VIEW}
            >
              {selections.map((fixture) => (
                <FilterSelection
                  key={fixture.id}
                  onClick={() => handleFixtureFilterSelection(fixture)}
                  showCheckbox
                  checked={isFixtureSelected(fixture)}
                  label={`${fixture.homeTeamName}-v-${fixture.awayTeamName}`}
                >
                  <div>
                    <h4>
                      {fixture.homeTeamName} v {fixture.awayTeamName}
                    </h4>
                    <p>
                      {`${DateTextMap.get(
                        dayjs(fixture.date).format("YYYY-MM-DD")
                      )} at ${dayjs(fixture.date).format("HH:mm")}`}
                    </p>
                  </div>
                </FilterSelection>
              ))}
            </FilterCategory>
          );
        })}
      </div>
    </>
  );
};

export default FilterFixtures;
